import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { formatMoney, getDateFromTimestamp, hexToRGBA, sendToGoogleAnalytics, AppStoreType, AppStateType, getRandomId } from "../../utils/utils";
import Fragment from "../../utils/fragment";
import { sendBeacon } from "../../store/storeUtils";
import { getUseAnalytics } from "../../store/getters";
import { setPopupOpen } from "../../store/modals";
import { getPopupItem, setPopupItemId, updatePopItemLink, changePopupItem, setProducts, getPopupProduct, directBuyIsOpen, setPopupProductId, isPreviousEnable, setIsModalOpenEventSent } from "../../store/popup";
import { StarRating } from "../../utils/funcComponents";
import { Icon } from "./popupFuncComponents";
import "swiped-events";
import { Item, Product, Settings, Unsubscribe } from "../../utils/types";


@Component({
  tag: "covet-pics-popup",
  styleUrl: "covet-gallery-popup.scss",
  shadow: true
})

export class CovetPicsPopup {
  @Element() el: HTMLCovetPicsPopupElement;
  /**
   * App store
   */
  @Prop() appStore: AppStoreType;
  private appState: AppStateType;
  private unsubscribe: Unsubscribe;
  private isModalOpenEventSent: boolean = false;
  private isModalChanged: boolean = false;

  @State() item: Item;
  @State() product: Product;
  @State() directBuyIsOpen: boolean = false;
  @State() popupTabIndex: number = -1;
  @State() settings: Settings;
  @State() isPreviousEnable: boolean;

  // money format template
  private moneyFormat: string;
  private isDefaultCurrency: boolean = true;
  private galleryEmbedId: number;
  // unique ID
  private contentCarouselId = null;

  /**
   * emit when popup change item to previous
   */
  @Event() previousPopupEvent: EventEmitter;
  /**
   * emit when popup change item to next
   */
  @Event() nextPopupEvent: EventEmitter;

  /**
   * emitted when Popup is opened and after all child component have rendered (JS API)
   */
  @Event({
    eventName: "modalOpen:covetPics",
    composed: false,
    cancelable: true,
    bubbles: true,
  }) modalOpen: EventEmitter;

  /**
   * emitted when Popup is closed (JS API)
   */
  @Event({
    eventName: "modalClose:covetPics",
    composed: false,
    cancelable: true,
    bubbles: true,
  }) modalClose: EventEmitter;

  /**
   * emitted when Popup is Changed (item) (JS API)
   */
  @Event({
    eventName: "modalChanged:covetPics",
    composed: false,
    cancelable: true,
    bubbles: true,
  }) modalChanged: EventEmitter;


  /** Open popup */
  @Method()
  async openPopup(popupId: string) {
    // TODO: Make sure we have an item in `this.items` array (?)
    this.appStore.dispatch(setPopupItemId(parseInt(popupId)));

    const popup = this.el.shadowRoot.querySelector(".popup") as HTMLElement;

    this.el.classList.add("covet-pics-popup-show", "covet-pics-popup-open");
    popup.classList.add("open");
    // prevent body to scroll setting overflow: hidden
    document.body.classList.add("covet-pics-popup-scroll-lock");

    this.appStore.dispatch(setPopupOpen(true));

    if (getUseAnalytics(this.appState)) {
      const category = "Covet.pics Popup";
      const action = "Covet.pics Popup Open";
      const label = "Covet.pics Popup Open - " + this.item.id;
      sendToGoogleAnalytics(action, category, label);
    }

    this.sendModalOpenBeacon();
    this.bindKeyboardArrowKeys();
    // ADA issue 
    this.popupTabIndex = 1;
    const focusableElements = this.getFocusableElements(popup);
    const closeBtn = focusableElements.find(el => el.getAttribute("aria-label") === "Close");
    closeBtn.focus();
  }

  /** Close popup */
  @Method()
  async closePopup() {
    this.pauseVideosInOpenPopup();

    this.el.classList.remove("covet-pics-popup-open");

    setTimeout(() => {
      this.appStore.dispatch(setPopupItemId(-1));
      this.appStore.dispatch(setIsModalOpenEventSent(false));
      const popup = this.el.shadowRoot.querySelector(".popup") as HTMLElement;
      popup.classList.remove("open");
      this.el.classList.remove("covet-pics-popup-show");

    }, this.animationDuration);

    // TODO: dispatch event afterClose

    // get back to normal scroll state removing body overflow
    document.body.classList.remove("covet-pics-popup-scroll-lock");

    this.modalClose.emit();
    this.appStore.dispatch(setPopupOpen(false));
    this.unBindKeyboardArrowKeys();
    this.popupTabIndex = -1;
  }

  /**
   *  listen to addToCart event
   *  render cart item count
   */
  @Listen("addToCart:covetPics", { target: "document" })
  addToCartHandler(event) {
    if (!this.isActive) { return false; }

    this.renderDirectBuyCart();

    if (getUseAnalytics(this.appState)) {
      const category = "Covet.pics Popup";
      const action = "Covet.pics Add-to-Cart Click";
      const label = "Covet.pics Add-to-Cart Click - " + event.detail.productId;
      sendToGoogleAnalytics(action, category, label);
    }

    this.sendDirectBuyBeacon(event.detail.productId);
  }

  @Listen("productLinkClicked", { target: "body" })
  productLinkClickedHandler(event) {
    if (!this.isActive) { return false; }

    if (getUseAnalytics(this.appState)) {
      const category = "Covet.pics Popup";
      const action = "Covet.pics Link Click";
      const label = "Covet.pics Link Click - " + event.detail.gaCampaign;
      sendToGoogleAnalytics(action, category, label);
    }

    this.sendProductLinkClickBeacon(event.detail.id);
  }

  @Watch("item")
  itemChangedHandler() {
    this.loadProducts();
  }

  componentWillLoad() {
    this.initStore();
    this.setMoneyFormat();
    this.contentCarouselId = `covet-popup-content-carousel-${getRandomId(16)}`;

    // insert <style> for lock scroll in document head
    document.head.insertAdjacentHTML("beforeend", "<style>.covet-pics-popup-scroll-lock{overflow:hidden}</style>");
  }
  
  componentDidLoad() {
    this.bindSwipeGestures();
    this.focusTrap();
  }
  
  componentDidRender() {
    if (this.isActive && !this.isModalOpenEventSent) {
      this.modalOpen.emit();
      this.appStore.dispatch(setIsModalOpenEventSent(true));
    }

    if (this.isActive && this.isModalChanged) {
      this.modalChanged.emit();
      this.isModalChanged = false;
    }
  }

  disconnectedCallback() {
    this.unsubscribe();
  }
  
  private initStore() {
    this.appState = this.appStore.getState();
    this.galleryEmbedId = this.appState.widgetId.embedId;
    this.settings = this.appState.settings;
    this.item = getPopupItem(this.appState);
    this.product = getPopupProduct(this.appState);
    this.directBuyIsOpen = directBuyIsOpen(this.appState);
    this.isPreviousEnable = isPreviousEnable(this.appState);
    this.isModalOpenEventSent = this.appState.popup.isModalOpenEventSent;
    this.unsubscribe = this.appStore.subscribe(() => {
      this.appState = this.appStore.getState();
      this.item = getPopupItem(this.appState);
      this.product = getPopupProduct(this.appState);
      this.directBuyIsOpen = directBuyIsOpen(this.appState);
      this.settings = this.appState.settings;
      this.isPreviousEnable = isPreviousEnable(this.appState);
      this.isModalOpenEventSent = this.appState.popup.isModalOpenEventSent;
    });
  }

  private get animationDuration() {
    return this.settings.popup_animation ? 300 : 0;
  }

  private get isActive() {
    return this.el.classList.contains("covet-pics-popup-open");
  }

  /**
   *  Click handlers
   */

  private getFocusableElements(element: HTMLElement): HTMLElement[] {
    const allFocusableElements = Array.from(element.querySelectorAll("a[href], button, input, textarea, select, details,[tabindex]:not([tabindex=\"-1\"])")) as HTMLElement[];
    const isElFocusable = (el: HTMLElement): boolean => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true" && el.offsetHeight !== 0; // JS, CSS tricks (offsetHeight) to detect if element not inside parent (display: none) 
    const focusableElements = allFocusableElements.filter(el => isElFocusable(el));
    return focusableElements;
  }

  private focusTrap() {
    this.el.addEventListener("keydown", (e) => {
      let isTabPressed = e.key === "Tab" || e.keyCode === 9;
      if (!isTabPressed) return;

      const shadow = document.activeElement.shadowRoot;
      const popup = this.el.shadowRoot.querySelector(".popup") as HTMLElement;
      const focusableElements = this.getFocusableElements(popup);
      const firstFocusableElement = focusableElements[0] as HTMLElement;
      const lastFocusableElement = focusableElements[focusableElements.length - 1] as HTMLElement;
      const activeElement = shadow.activeElement;
      if (e.shiftKey) {
        if (activeElement === firstFocusableElement) {
          lastFocusableElement.focus();
          e.preventDefault();
        }
      } 
      else {
        if (activeElement === lastFocusableElement) { 
          firstFocusableElement.focus();
          e.preventDefault();
        }
      }
    });
  }

  private playPauseVideoClickHandler = (event: UIEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget as HTMLElement;
    const videoWrapper = button.parentElement as HTMLVideoElement;

    return videoWrapper.classList.contains("playing") ? this.pauseVideo(videoWrapper) : this.playVideo(videoWrapper);
  }

  private closeDirectBuy() {
    return this.appStore.dispatch(setPopupProductId(-1));
  }

  private previousClickHandler = (event: UIEvent) => {
    event.preventDefault();
    event.stopPropagation();
    this.pauseVideosInOpenPopup();

    this.appStore.dispatch(changePopupItem("previous"));
    this.closeDirectBuy();
    
    this.isModalChanged = true;
    this.previousPopupEvent.emit(event);
  }

  private nextClickHandler = async(event: UIEvent) => {
    event.preventDefault();
    event.stopPropagation();
    this.pauseVideosInOpenPopup();

    this.appStore.dispatch(changePopupItem("next"));
    this.closeDirectBuy();

    this.isModalChanged = true;
    this.nextPopupEvent.emit(event);
  }

  private hidePopupClickHandler = (event: UIEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const element = event.currentTarget as HTMLElement;
    if (element.getAttribute("data-dismiss") === "popup") {
      this.closePopup();
      this.closeDirectBuy();
    }
  }

  private sendDirectBuyBeacon(productId) {
    const beaconData = {
      gallery_item_id: this.item.id,
      product_id: productId,
    };

    sendBeacon(this.appState, "direct_buy", beaconData);
  }

  private sendModalOpenBeacon() {
    const data = {
      gallery_item_id: this.item.id,
    };

    sendBeacon(this.appState, "modal_open", data);
  }

  private sendProductLinkClickBeacon(id) {
    const data = {
      gallery_item_id: this.item.id,
      link_id: id,
    };

    sendBeacon(this.appState, "grid_link_click", data);
  }

  private pauseVideosInOpenPopup() {
    const popupVideos = this.el.shadowRoot.querySelectorAll(".post-video-element") as NodeListOf<HTMLElement>;

    popupVideos.forEach(videoWrapper => this.pauseVideo(videoWrapper));
  }

  private pauseVideo(videoWrapperEl: HTMLElement) {
    const videoEl = videoWrapperEl.querySelector("video") as HTMLVideoElement;

    videoEl.pause();
    videoWrapperEl.classList.remove("playing");
  }

  private playVideo(videoWrapperEl: HTMLElement) {
    const videoEl = videoWrapperEl.querySelector("video") as HTMLVideoElement;
    let playPromise = videoEl.play();

    if (playPromise !== null) {
      playPromise.catch(() => { videoEl.play(); });
    }
    videoWrapperEl.classList.add("playing");
  }

  private setMoneyFormat() {
    this.moneyFormat = this.settings.money_format;
    // detect if Shopify site and if it's using Shopify multi-currency
    // example of window.Shopify.currency object: { active: "GBP", rate: "1.0" }
    const shopify = (window as any).Shopify;
    if (typeof shopify !== "undefined") {
      // now check active currency on Shopify /cart.js endpoint
      let endpoint = "/cart.js";
      if (shopify.routes && shopify.routes.root && shopify.routes.root !== "/") {
        endpoint = `${shopify.routes.root}${endpoint}`;
      }
      fetch(endpoint)
        .then(response => response.json())
        .then(cartData => {
          // change moneyFormat, if rate != 1.0
          // or if currency from /cart.js doesn't match window.Shopify.currency (edge-case bug)
          if (shopify.currency["rate"] !== "1.0" || cartData.currency !== shopify.currency["active"]) {
            this.moneyFormat = `{{amount}} ${cartData.currency}`;
            this.isDefaultCurrency = false;
          }
        });
    }
  }


  private setPopupCSSVariables() {
    let cssVariables = {
      "--covet-pics-popup-background-color": hexToRGBA(this.settings.popup_background_color, (this.settings.popup_background_opacity / 100).toString()),

      // close button
      "--covet-pics-close-button-symbol-color": this.settings.close_button_custom_color,
      "--covet-pics-close-button-background-color": this.settings.close_button_custom_box_color,

      // arrows - desktop
      "--covet-pics-arrows-desktop-symbol-color": this.settings.arrow_custom_color,
      "--covet-pics-arrows-desktop-background-color": this.settings.arrow_custom_box_color,
      "--covet-pics-arrows-desktop-size": this.settings.arrow_size_custom.toString() + "px",

      // arrows - mobile
      "--covet-pics-arrows-mobile-symbol-color": this.settings.mobile_arrow_custom_color,
      "--covet-pics-arrows-mobile-background-color": this.settings.mobile_arrow_custom_box_color,
      "--covet-pics-arrows-mobile-size": "48px",
    };

    if (this.settings.font_family !== "") {
      cssVariables["--covet-pics-font-family"] = `${this.settings.font_family}, sans-serif`;
    }

    return cssVariables;
  }

  private imgAltTag(altTag: string, caption: string, idx: number, itemsLength: number, createdTime: string) {
    if (altTag && altTag !== null) return altTag;
    
    const carouselAlt = idx !== -1
      ? `, slide ${idx + 1}/${itemsLength}`
      : "";
  
    return caption && caption.length > 0 
      ? `${caption}, created ${getDateFromTimestamp(createdTime)}${carouselAlt}`
      : `post image, created ${getDateFromTimestamp(createdTime)}${carouselAlt}`;
  }

  private renderImageElements() {
    const { alt_tag, caption, carousel_media, data_type, created_time, cover_images, images, video } = this.item;

    if (carousel_media && carousel_media.length > 0) {
      return <covet-pics-popup-slider item={this.item}></covet-pics-popup-slider>;
    } 

    if (data_type === "image" || data_type === "carousel") {
      return (<img class="post-image-element" src={images.high_resolution} alt={this.imgAltTag(alt_tag, caption, -1, carousel_media.length, created_time)} />);
    }

    if (data_type === "video") {
      return (
        <div class="post-video-element">
          <video playsinline loop poster={cover_images.high_resolution} key={video} title={alt_tag}> 
            <source src={video} type="video/mp4" />
          </video>
          <button type="button" class="post-video-play" aria-label="Play video" onClick={this.playPauseVideoClickHandler}></button>
          <button type="button" class="post-video-pause" aria-label="Pause video" onClick={this.playPauseVideoClickHandler}></button>
        </div>
      );
    }

    return null;
  }

  private renderImage() {
    // TODO maybe refactor logic or var name
    const arrowsBottomMargin = this.settings._arrowsDesktopPosition === "bottom" && this.item.data_type === "carousel" ? "bottom-margin" : "";
    const postImageCarouselClass = this.item.data_type !== "text" && this.item.carousel_media && this.item.carousel_media.length > 0 ? "post-image-carousel" : "";
    return (
      <section class={`post-image arrows-mobile-position-${this.settings._arrowsMobilePosition} ${postImageCarouselClass}`} key={this.item.id} tabindex="0">
        {this.settings._arrowsDesktopType === "arrows-type-4" &&
          <div class={`desktop-arrows arrows-size-${this.settings._arrowsDesktopSizeType} arrows-color-theme-${this.settings.arrow_color_theme} ${this.settings._arrowsDesktopPosition} arrows-type-4 ${arrowsBottomMargin}`}>
            {this.renderArrows(this.settings._arrowsDesktopType)}
          </div>
        }
        {this.item.data_type === "text" && 
          <covet-pics-popup-review
            rating={this.item.rating}
            caption={this.item.caption}
            username={this.item.username}
            createdTime={this.item.created_time}
            verified={this.item.verified}
            item-type={this.item.data_type }
          ></covet-pics-popup-review>
        }
        {this.item.data_type !== "text" && this.renderImageElements()}
        {this.settings.hotspots.enabled && this.item.data_type !== "text" &&
          <covet-pics-hotspots links={this.item.links} 
            background-color={this.settings.hotspots.background_color} 
            text-color={this.settings.hotspots.text_color} 
            show-numbers={this.settings.hotspots.show_numbers}>
          </covet-pics-hotspots>
        }
        {!this.settings.hide_branding &&
          <a class={`covet-branding ${postImageCarouselClass}`} href={`https://apps.shopify.com/covet-pics?utm_source=embed&utm_medium=popup&utm_campaign=${location.host}`} target="_blank" rel="noopener" tabindex="0" aria-label="Covet.pics logo link"></a>
        }
      </section>
    );
  }

  private renderSidebar() {
    const starOnly = this.item.caption === null && this.item.data_type === "text";
    const greyBorderClass = this.item.caption !== null && this.item.rating !== null && this.item.data_type !== "text" ? "grey-border" : "";
    if (!this.item.caption && !this.item.rating && this.item.links.length === 0) return null;
    const linkId = getRandomId(16);

    return (
      <section class="popup-body" key={this.item.id}>
        {this.settings._arrowsMobilePosition === "middle" &&
          this.renderMobileArrows()
        }
        <div class="popup-body-content" aria-hidden={`${this.directBuyIsOpen}`}>
          {this.settings._arrowsDesktopType === "arrows-type-5" && this.settings._arrowsDesktopPosition === "top" &&
            <div class={`desktop-arrows arrows-size-${this.settings._arrowsDesktopSizeType} arrows-color-theme-${this.settings.arrow_color_theme}`}>
              {this.renderArrows(this.settings._arrowsDesktopType)}
            </div>
          }
          <div class="popup-body-content-scroll">
            {!this.settings.popup_products_title_disabled && (this.item.links.length > 0) &&
              <h3 class="shop-this-look">{this.settings.popup_products_title}</h3>
            }
            {this.item.links.length > 0 &&
              <covet-pics-popup-links
                links={this.item.links}
                item-created-time={this.item.created_time}
                grey-border-class={greyBorderClass}
                appStore={this.appStore}>
              </covet-pics-popup-links>
            }

            {this.item.rating && !starOnly && !(this.item.data_type === "text") &&
            <StarRating rating={this.item.rating} ratingClass="star-rating"/>
            }
              
            {this.item.caption && !(this.item.data_type === "text") && 
              <Fragment>
                <p class="caption" innerHTML={this.item.caption}></p>
                {!(this.item.source === "instagram") && 
                  <p class="source">
                    {this.item.verified && <Icon iconName="verification"/>}
                    {this.item.username}
                  </p>
                }
              </Fragment>
            }
            {this.item.source === "instagram" &&
              <p class="source">
                <a class="source-link" target="_blank" href={this.item.url} aria-describedby={`link-description-${linkId}`} rel="noopener noreferrer">
                  @{this.item.username}
                  <Icon iconName="external-link"/>
                  <span id={`link-description-${linkId}`} class="sr-only">Instagram link, this link opens in a new window</span>
                </a>
              </p>}
            {this.item.source === "file" && this.item.username !== null &&
              <p class="source">
                {this.item.verified && <Icon iconName="verification"/>}
                {this.item.username}
              </p>
            }
          </div>
          {this.settings._arrowsDesktopType === "arrows-type-5" && this.settings._arrowsDesktopPosition === "bottom" &&
            <div class={`desktop-arrows arrows-size-${this.settings._arrowsDesktopSizeType} arrows-color-theme-${this.settings.arrow_color_theme}`}>
              {this.renderArrows(this.settings._arrowsDesktopType)}
            </div>
          }
          {!this.settings.hide_social_links && <covet-pics-popup-shared-links item={this.item} galleryEmbedId={this.galleryEmbedId} />}
        </div>
        
        {this.product !== null &&
          <covet-pics-direct-buy
            money-format={this.moneyFormat}
            product={this.product}
            appStore={this.appStore}>
          </covet-pics-direct-buy>
        }
      </section>
    );
  }

  private renderArrows(arrowsType: string) {
    return [
      (
        <button class={`ctrl-arrow ctrl-prev ${arrowsType}`} onClick={this.previousClickHandler} 
          aria-controls={this.contentCarouselId} type="button" aria-label="Previous widget item" disabled={!this.isPreviousEnable}>
          <Icon iconName={`${arrowsType}-left`}/>
        </button>
      ),
      (
        <button class={`ctrl-arrow ctrl-next ${arrowsType}`} onClick={this.nextClickHandler} 
          aria-controls={this.contentCarouselId} type="button" aria-label="Next widget item">
          <Icon iconName={`${arrowsType}-right`}/>
        </button>
      )
    ];
  }

  private renderMobileArrows() {
    return (
      <div class={`mobile-navigation arrows-mobile-position-${this.settings._arrowsMobilePosition} arrows-color-theme-${this.settings._arrowsMobileColorTheme}`}>
        <div class="mobile-arrows">
          {this.renderArrows(this.settings._arrowsMobileType)}
        </div>
        <div class="mobile-buttons">
          <a class="btn btn-checkout" href="/cart" aria-label="View cart">
            <span class="counter"></span>
            <Icon iconName="checkout"/>
          </a>
          <button class="btn btn-close btn-type-3" data-dismiss="popup" aria-label="Close" onClick={this.hidePopupClickHandler} type="button">
            <Icon iconName="btn-type-3" />
          </button>
        </div>
      </div>
    );
  }

  private renderCloseButton() {
    return (
      <button class={`btn-popup-close ${this.settings._closeButtonType} ${this.settings.close_button_size} ${this.settings.close_button_position} ${this.settings._closeButtonСolorTheme}`} 
        data-dismiss="popup" aria-label="Close" onClick={this.hidePopupClickHandler} type="button">
        <Icon iconName={this.settings._closeButtonType} />
      </button>
    );
  }

  private renderDirectBuyCart() {
    const checkoutButtons = Array.from(this.el.shadowRoot.querySelectorAll(".btn-checkout"));
    checkoutButtons.forEach(checkoutButton => {
      checkoutButton.classList.add("show");
      checkoutButton.setAttribute("aria-hidden", "false");
    });

    fetch("/cart.js")
      .then(response => response.json())
      .then(cartData => {
        const checkoutItemCounters = Array.from(this.el.shadowRoot.querySelectorAll(".btn-checkout .counter"));
        checkoutItemCounters.forEach(counter => {
          counter.classList.remove("bounce");
        });

        setTimeout(() => {
          checkoutItemCounters.forEach(counter => {
            counter.classList.add("bounce");
            counter.innerHTML = cartData.items.length;
          });
        }, 50);
      });
  }

  private bindSwipeGestures() {
    this.el.addEventListener("swiped-left", async(event) => {
      const uiEvent = event as UIEvent;

      // ignore swipes within carousel post
      // TODO check this
      const isCarousel = this.item.carousel_media && this.item.carousel_media.length > 0;
      if (isCarousel) {
        const popupCarousel = this.el.shadowRoot.querySelector("covet-pics-popup-slider");
        const isSliderIsEnd = await popupCarousel.getSwiperState("end");
        if (popupCarousel !== null && !isSliderIsEnd) return false;
      }
      
      // ignore swipes if direct buy is open
      if (this.directBuyIsOpen === true) { return false; }
      
      this.nextClickHandler(uiEvent);
    });
    
    this.el.addEventListener("swiped-right", async(event) => {
      if (!this.isPreviousEnable) return false;
      const uiEvent = event as UIEvent;
      
      // ignore swipes within carousel post
      // TODO check this
      const isCarousel = this.item.carousel_media && this.item.carousel_media.length > 0;
      if (isCarousel) {
        const popupCarousel = this.el.shadowRoot.querySelector("covet-pics-popup-slider");
        const isSliderIsBeging = await popupCarousel.getSwiperState("begin");
        if (popupCarousel !== null && !isSliderIsBeging) return false;
      }

      // ignore swipes if direct buy is open
      if (this.directBuyIsOpen === true) { return false; }

      this.previousClickHandler(uiEvent);
    });
  }

  private keyboardEventsHandler = (event: KeyboardEvent) => {
    if (event.key === "Escape" || event.keyCode === 27) return this.closePopup();
    if ((event.key === "ArrowLeft" || event.keyCode === 37) && this.isPreviousEnable) return this.previousClickHandler(event);
    if (event.key === "ArrowRight" || event.keyCode === 39) return this.nextClickHandler(event);
    return true;
  }

  private bindKeyboardArrowKeys() {
    document.addEventListener("keydown", this.keyboardEventsHandler);
  }

  private unBindKeyboardArrowKeys() {
    document.removeEventListener("keydown", this.keyboardEventsHandler);
  }

  private loadProducts() {
    if (!this.settings.direct_add_to_cart && this.isDefaultCurrency) { return false; }
    if (this.item === null) return false;

    this.item.links.forEach(link => {
      // TODO add new props to link in store
      if (link.shopify_product_id === null) { return true; }
      if (link.product_is_loaded) { return true; }

      fetch(`${link.link}.js`)
        .then(response => response.json())
        .then(data => {
          const prices = Object.assign({}, link.prices, {
            price_label: formatMoney(data.price, this.moneyFormat, false),
            sale_price: formatMoney(data.compare_at_price, this.moneyFormat, false)
          });

          this.appStore.dispatch(updatePopItemLink({ itemId: this.item.id, linkId: link.id, properties: 
            { product_is_loaded: true, prices } 
          }));

          data.link = link.link;
          this.appStore.dispatch(setProducts([...this.appState.popup.products, data]));
        });
    });
  }

  render() {
    return (
      // needsclick FIXME: https://github.com/ftlabs/fastclick/issues/604 
      <Host style={this.setPopupCSSVariables()} class="needsclick"> 
        <div class={{ "popup": true, "responsive": this.settings.popup_image_size === "responsive", "animated": this.settings.popup_animation, "direct-buy-open": this.directBuyIsOpen }} tabindex={this.popupTabIndex} role="dialog" aria-label="Popup">
          {this.settings.close_button_position === "corner" &&
            this.renderCloseButton()
          }
          {this.settings._arrowsMobilePosition === "top" &&
            this.renderMobileArrows()
          }
          <div class="popup-dialog">
            {this.item && this.item.id !== -1 &&
              <div id={this.contentCarouselId} class="popup-content">
                {this.renderImage()}
                {this.renderSidebar()}
              </div>
            }
            {["arrows-type-1", "arrows-type-2", "arrows-type-3"].includes(this.settings._arrowsDesktopType) &&
              <div class={`desktop-arrows arrows-size-${this.settings._arrowsDesktopSizeType} arrows-color-theme-${this.settings.arrow_color_theme}`}>
                {this.renderArrows(this.settings._arrowsDesktopType)}
              </div>
            }
            {this.settings.close_button_position === "side" &&
              this.renderCloseButton()
            }
            <a href="/cart" target="_top" class={`btn-checkout desktop ${this.settings.close_button_position} ${this.settings.close_button_size} ${this.settings._closeButtonСolorTheme}`} aria-hidden="true" aria-label="View cart">
              <span class="counter"></span>
              <Icon iconName="checkout"/>
            </a>
          </div>
          <div class="backdrop" data-dismiss="popup" onClick={this.hidePopupClickHandler}></div>
        </div>
      </Host>
    );
  }
}
